/**
 * Store features on the storefront (ADR 0009 §5, D101). The public store context
 * (/api/meta/context) says which features are on (`store.features`) and which
 * storefront pages sit behind each (`store.feature_paths`, declared once on the
 * server in lib/store/features). A page under a closed feature is not routed and
 * no link leads to it, except the patterns the feature keeps reachable while off
 * (`store.feature_kept_paths`, ":id" one segment): with repairs off a shopper still
 * opens their own repair at /repair/:id. A feature that is missing or not exactly true
 * is off (fail closed). The server answers 404 for the feature's models either way,
 * apart from the shopper actions a model declares as surviving (featureOffActions).
 */

/** Is the feature on? */
export const featureOn = (features, key) => Boolean(features && key && features[key] === true);

const offLists = (store, field) => {
  const paths = (store && store[field]) || {};
  return Object.entries(paths).filter(([key]) => !featureOn(store.features, key)).flatMap(([, list]) => (Array.isArray(list) ? list : []));
};

/** The storefront paths closed for this store: every path of every feature that is not on. */
export const closedPaths = (store) => offLists(store, 'feature_paths');

/** The patterns that stay reachable under those closed paths. */
export const keptPaths = (store) => offLists(store, 'feature_kept_paths');

const segments = (p) => p.split('/').filter(Boolean);
/** Path vs pattern, segment by segment (":x" is any one segment). Mirrors lib/store/features on the server. */
const matchesPattern = (path, pattern) => {
  const a = segments(path);
  const b = segments(pattern);
  return a.length === b.length && b.every((seg, i) => (seg.startsWith(':') ? a[i].length > 0 : seg === a[i]));
};

const pathOf = (href) => String(href || '').split(/[?#]/)[0];
const isInternal = (href) => typeof href === 'string' && href.startsWith('/') && !href.startsWith('//');
const under = (path, p) => path === p || path.startsWith(`${p}/`);

/**
 * href → may the shopper go there? False only for an internal path under a closed one that
 * no kept pattern reopens; other sites are not ours to hide.
 */
export function linkAllowed(store) {
  const closed = closedPaths(store);
  const kept = keptPaths(store);
  return (href) => {
    if (!isInternal(href)) return true;
    const path = pathOf(href);
    return !closed.some((p) => under(path, p)) || kept.some((k) => matchesPattern(path, k));
  };
}

/** The routes this store mounts: those a shopper may visit (the route pattern itself is judged: "/repair/:id" is one segment deeper than "/repair"). */
export const enabledRoutes = (routes, store) => {
  const allowed = linkAllowed(store);
  return routes.filter((r) => allowed(r.path));
};

const LINK_KEYS = ['href', 'cta_href'];
const blocked = (node, allowed) => LINK_KEYS.some((k) => typeof node[k] === 'string' && node[k] && !allowed(node[k]));
// Hero slides are content first: a closed link loses its button, never the slide.
const KEEP_ITEMS_OF = new Set(['slides']);

/**
 * The store document without links to closed pages (a kit link written before the
 * feature went off, or one the owner added): a link or card in a list is dropped;
 * a slide, or a single link such as an announcement or a banner button, keeps its
 * content and loses the link. A list item whose own list a drop empties goes too
 * (a footer column with no links, a section with no cards); at the top an emptied
 * list just stays empty, so the storefront falls back to its own (filtered) links.
 */
export function withoutClosedLinks(doc, allowed) {
  const DROP = Symbol('drop');
  // → { value, emptied } where `emptied` says a list below lost all its items to drops.
  const walk = (node, inList, listKey) => {
    if (Array.isArray(node)) {
      const kept = node.map((n) => walk(n, true, listKey)).filter((r) => r.value !== DROP);
      return { value: kept.map((r) => r.value), emptied: node.length > 0 && kept.length === 0 };
    }
    if (!node || typeof node !== 'object') return { value: node, emptied: false };
    let src = node;
    if (blocked(node, allowed)) {
      if (inList && !KEEP_ITEMS_OF.has(listKey)) return { value: DROP, emptied: false };
      src = { ...node };
      for (const k of LINK_KEYS) if (typeof src[k] === 'string' && src[k] && !allowed(src[k])) src[k] = '';
      if ('cta_label' in src && !src.cta_href) src.cta_label = '';
    }
    const out = {};
    let emptied = false;
    for (const [k, v] of Object.entries(src)) {
      const r = walk(v, false, k);
      out[k] = r.value;
      emptied = emptied || r.emptied;
    }
    if (emptied && inList) return { value: DROP, emptied: false };
    return { value: out, emptied };
  };
  if (!doc || typeof doc !== 'object') return doc;
  return walk(doc, false, null).value;
}
